import { autocomplete } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import algoliasearch from 'algoliasearch';
import Cookies from 'js-cookie';
import * as React from 'react';
import { Fragment, createElement } from 'react';
import { render } from 'react-dom';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import { SEARCH_CREDENTIALS } from '@app/services/constants/algolia';
import { updateQuery } from '@app/store/actions/app.actions';

function updateUrlParameter(key: string, value: any) {
	// Get the current URL search params
	const searchParams = new URLSearchParams(window.location.search);

	// Update or add the parameter
	searchParams.set(key, value);

	// Build the new URL with the updated search params
	const newUrl = `${
		window.location.origin + window.location.pathname
	}?${searchParams.toString()}`;

	// Replace the current URL with the new URL
	window.history.replaceState({ path: newUrl }, '', newUrl);
}

export function Autocomplete(props: any) {
	const [query, setQuery] = React.useState('');
	const dispatch = useDispatch();
	const { selectedMode } = props;
	const currentQuery = useSelector((state: RootStateOrAny) => state.app.query);

	const containerRef = React.useRef(null);

	const searchClient = algoliasearch(
		SEARCH_CREDENTIALS.appID,
		SEARCH_CREDENTIALS.apiKey,
	);

	const handleSearch = React.useCallback(
		(q: string) => {
			updateUrlParameter('q', q);
			// Validate if you are in the searchPage (Otherwise redirect using q param)
			Cookies.set('searchQuery', q, { secure: true });
			dispatch(updateQuery(q));

			// If on a mobile, redirect all searches
			if (window.innerWidth <= 768) {
				window.location.href = `${selectedMode.action}?q=${q}`;
			} else {
				// Check if current page is a search page
				// eslint-disable-next-line no-lonely-if
				if (window.location.pathname === selectedMode.action) {
					// Update the state
					setQuery(q);
					// If not a search page, redirect to appropriate search page
				} else {
					window.location.href = `${selectedMode.action}?q=${q}`;
				}
			}
		},
		[dispatch, selectedMode.action],
	);

	const querySuggestionsPlugin = createQuerySuggestionsPlugin({
		searchClient,
		indexName: selectedMode.querySuggestionIndex,
		getSearchParams() {
			return { hitsPerPage: 3 };
		},
	});

	const liveQuery = React.useRef('');

	React.useEffect(() => {
		if (!containerRef.current) {
			return undefined;
		}

		const search = autocomplete({
			container: containerRef.current,
			plugins: [querySuggestionsPlugin],
			placeholder: 'What are you interested in?',
			initialState: { query: currentQuery },
			insights: true,
			renderer: { createElement, Fragment, render },
			onStateChange({ state }) {
				liveQuery.current = state.query;
			},
			onSubmit({ state }) {
				handleSearch(state.query);
			},
			...props,
		});

		const handleClickOutside = (event: any) => {
			if (
				containerRef.current &&
				// @ts-ignore containerRef is not null
				!containerRef.current.contains(event.target)
			) {
				search.setIsOpen(false);
			}
		};

		document.addEventListener('mouseup', handleClickOutside);

		return () => {
			document.removeEventListener('mouseup', handleClickOutside);
			search.destroy();
		};
	}, [
		props,
		selectedMode,
		dispatch,
		query,
		currentQuery,
		querySuggestionsPlugin,
		handleSearch,
	]);

	return (
		<div className="algolia-topbar-container">
			<div className="algolia-topbar" ref={containerRef} />
			<button
				onClick={() => handleSearch(liveQuery.current)}
				className="btn desktop-search-form__submit algolia-search-btn"
				type="button"
				aria-label="Search"
			>
				Search
			</button>
		</div>
	);
}
